import _, { isEmpty, cloneDeep } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import clsx from "clsx";
import {
  BaseDialog,
  Button,
  Checkbox,
  CloseIcon,
  CoolSwitch,
  DaysList,
  Divider,
  IconButton,
  RadioButton,
  SearchInput,
  Select,
  Table,
  TextField,
  TimePicker,
  Tooltip,
  Typography,
  useConfirmation,
} from "@coolremote/shared";
import { Contact as sdkContact } from "coolremote-sdk";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import useChurnZero from "@hooks/useChurnZero";
import AddEditContact from "./AddEditContact";
import FilterOutSpecificErrorCodesForm, { validateFilters } from "./components/FilterOutSpecificErrorCodesForm";
import { IReportSchedule, ReportFormat, ReportTimeUnit } from "../../models/AlertGroups";
import styles from "./AlertGroup2.module.css";

export interface IAlertGroupType {
  id: number;
  name: string;
  users: string[];
  notificationsEnabled: boolean;
  reportEnabled: boolean;
  reportSchedule?: IReportSchedule;
  customer: string;
}

type StepId = "trigger" | "realtime" | "report" | "recipients";

const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const pad = (n: number) => String(n).padStart(2, "0");

const ordinalSuffixOf = (i: number) => {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) return `${i}st`;
  if (j === 2 && k !== 12) return `${i}nd`;
  if (j === 3 && k !== 13) return `${i}rd`;
  return `${i}th`;
};

const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: ordinalSuffixOf(i + 1),
}));

const Segmented: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className={styles.segmented}>
    {options.map((option) => (
      <Button
        key={option.value}
        className={clsx(styles.segment, value === option.value && styles.segmentActive)}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </Button>
    ))}
  </div>
);

const AlertGroup: React.FC<any> = (props: any) => {

  const {
    close,
    editAlertGroup: alertGroup,
    saveAlert,
    allTraps,
    allSites,
    automationRules,
    siteId,
    userContacts,
    setUserContacts,
    smartRules
  } = props;
  const allSitesById = allSites?.reduce((acc: any, cur: any) => ({ ...acc, [cur.id]: cur }), {});
  const isEdit = alertGroup && !alertGroup.new ? true : false;

  const types = useStoreState((s) => s.types);
  const configFile = useStoreState((s: any) => s.config);
  const user = useStoreState((s) => s.users.me);
  const { customerId } = useStoreState((s) => s.selections.selections);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const deleteContactFromSite = useStoreActions((action) => action.contacts.deleteContact);
  const confirm = useConfirmation();
  const { trapPriorityTypes, alertGroupTypes, aggregationTimePeriods, timeFormat: timeFormatTypes } = types;
  const timeFormatObject = user?.timeFormat ? timeFormatTypes[user.timeFormat] : timeFormatTypes[0];
  const is12Hours = timeFormatObject?.text === "12 hours";

  const [activeStep, setActiveStep] = useState<StepId>("trigger");
  const [visitedSteps, setVisitedSteps] = useState<Record<StepId, boolean>>({
    trigger: true,
    realtime: isEdit,
    report: isEdit,
    recipients: isEdit,
  });

  const [name, setName] = useState(alertGroup ? alertGroup.name : "");
  const [error, setError] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [globalAlertsEnable, setGlobalAlertsEnable] = useState<any>({});
  const [allUserTrapsEnable, setAllUsersTrapsEnable] = useState(alertGroup ? !!alertGroup.allUserTrapsEnable : false);
  const [allLogicTrapsEnable, setAllLogicTrapsEnable] = useState(alertGroup ? !!alertGroup.allLogicTrapsEnable : false);
  const [allSmartRulesEnable, setAllSmartRulesEnable] = useState(alertGroup ? !!alertGroup.allSmartRulesEnable : false);
  const [selectedTrapPriorityType, setSelectedTrapPriorityType] = useState<number>(1);
  const [notifyBy, setNotifyBy] = useState<string>("1");
  const [specificAlertTypeSelected, setSpecificAlertTypeSelected] = useState<string>("System Error");
  const [specificAlertSearchValue, setSpecificAlertSearchValue] = useState<string>("");
  const [specificAlertTypesMap, setSpecificAlertTypesMap] = useState<any>({});
  const specificAlertTypes = ["System Error", "Indoor Error", "Anomalies", "Maintenance", "Communication", "Automation Logic", "Smart Rules"];

  const [filterSpecificErrorCodes, setFilterSpecificErrorCodes] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>([]);
  const [filterSpecificErrorCodesValidation, setFilterSpecificErrorCodesValidation] = useState({ isValid: true, details: [] })
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(alertGroup?.notificationsEnabled ?? true);
  const [aggregationEnabled, setAggregationEnabled] = useState<boolean>(alertGroup ? alertGroup.aggregationEnabled ?? true : true);
  const [aggregationPeriod, setAggregationPeriod] = useState<string>(alertGroup ? alertGroup.aggregationPeriod ?? '5m' : '5m');
  const [sendCloseNotifications, setSendCloseNotifications] = useState<boolean>(alertGroup ? alertGroup.sendCloseNotifications ?? true : true);
  const [smartAggregationEnabled, setSmartAggregationEnabled] = useState<boolean>(alertGroup ? alertGroup.smartAggregationEnabled ?? false : false);

  const [reportEnabled, setReportEnabled] = useState<boolean>(!!alertGroup?.reportEnabled);
  const [reportFormat, setReportFormat] = useState<ReportFormat>(alertGroup?.reportSchedule?.format || 'xlsx');
  const initialTimeUnit = (schedule?: IReportSchedule): ReportTimeUnit => {
    if (schedule?.timeUnit === 'week') return 'week';
    if (schedule?.timeUnit === 'month' || (schedule?.timeUnit === 'custom' && schedule?.customMode === 'monthDays')) return 'month';
    return 'day';
  };
  const initialDayOfMonth = (schedule?: IReportSchedule): number =>
    schedule?.dayOfMonth ?? schedule?.daysOfMonth?.[0] ?? 1;
  const initialMonthMode = (schedule?: IReportSchedule): 'end' | 'specific' =>
    schedule?.endOfMonth ? 'end' : 'specific';
  const [reportTimeUnit, setReportTimeUnit] = useState<ReportTimeUnit>(initialTimeUnit(alertGroup?.reportSchedule));
  const [reportMinute, setReportMinute] = useState<number>(alertGroup?.reportSchedule?.minute ?? 0);
  const [reportHour, setReportHour] = useState<number>(alertGroup?.reportSchedule?.hour ?? 6);
  const [reportDayOfWeek, setReportDayOfWeek] = useState<number>(alertGroup?.reportSchedule?.dayOfWeek ?? 1);
  const [reportDayOfMonth, setReportDayOfMonth] = useState<number>(initialDayOfMonth(alertGroup?.reportSchedule));
  const [reportMonthMode, setReportMonthMode] = useState<'end' | 'specific'>(initialMonthMode(alertGroup?.reportSchedule));
  const [reportScheduleTouched, setReportScheduleTouched] = useState<boolean>(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState<boolean>(false);

  const [contacts, setContacts] = useState<any[]>(alertGroup?.contacts ? [...alertGroup.contacts] : []);
  const [recipientSearch, setRecipientSearch] = useState<string>("");
  const [addingRecipient, setAddingRecipient] = useState<boolean>(false);
  const [addRecipientsSearch, setAddRecipientsSearch] = useState<string>("");
  const [contactsToAdd, setContactsToAdd] = useState<string[]>([]);
  const [contactIdsToDelete, setContactIdsToDelete] = useState<string[]>([]);
  const [editContact, setEditContact] = useState<any>(null);
  const [addingNewContact, setAddingNewContact] = useState<boolean>(false);
  const recipientsToolbarRef = useRef<HTMLDivElement>(null);

  const { trackEvent } = useChurnZero();

  const hasStoredReportSchedule = !!alertGroup?.reportSchedule;
  const mustSendReportSchedule = reportEnabled && (!hasStoredReportSchedule || reportScheduleTouched);

  const reportFormatOptions = [
    { value: 'xlsx', label: t`Excel (.xlsx)` },
    { value: 'csv', label: t`CSV (.csv)` },
  ];
  const reportFrequencyOptions = [
    { value: 'day', label: t`Daily` },
    { value: 'week', label: t`Weekly` },
    { value: 'month', label: t`Monthly` },
  ];
  const weekdayShortLabels = [t`Sun`, t`Mon`, t`Tue`, t`Wed`, t`Thu`, t`Fri`, t`Sat`];

  const markReportScheduleTouched = () => setReportScheduleTouched(true);

  const buildReportSchedulePayload = (): IReportSchedule => {
    const schedule: IReportSchedule = {
      timeUnit: reportTimeUnit,
      format: reportFormat,
      hour: reportHour,
      minute: reportMinute,
    };
    if (reportTimeUnit === 'week') {
      schedule.dayOfWeek = reportDayOfWeek;
    }
    if (reportTimeUnit === 'month') {
      // Monthly takes either endOfMonth or dayOfMonth — the backend rejects both together.
      if (reportMonthMode === 'end') {
        schedule.endOfMonth = true;
      } else {
        schedule.dayOfMonth = reportDayOfMonth;
      }
    }
    return schedule;
  };

  const init = () => {
    setName(alertGroup?.name || "");
    setFilterSpecificErrorCodes(alertGroup?.enableFilters || false);
    setFilters(alertGroup?.filters || []);
    setNotificationsEnabled(alertGroup?.notificationsEnabled ?? true);
    setAggregationEnabled(alertGroup?.aggregationEnabled ?? true);
    setAggregationPeriod(alertGroup?.aggregationPeriod ?? '5m');
    setSendCloseNotifications(alertGroup?.sendCloseNotifications ?? true);
    setSmartAggregationEnabled(alertGroup?.smartAggregationEnabled ?? false);

    const schedule = alertGroup?.reportSchedule;
    setReportEnabled(!!alertGroup?.reportEnabled);
    setReportFormat(schedule?.format || 'xlsx');
    setReportTimeUnit(initialTimeUnit(schedule));
    setReportMinute(schedule?.minute ?? 0);
    setReportHour(schedule?.hour ?? 6);
    setReportDayOfWeek(schedule?.dayOfWeek ?? 1);
    setReportDayOfMonth(initialDayOfMonth(schedule));
    setReportMonthMode(initialMonthMode(schedule));
    setReportScheduleTouched(false);

    setContacts(alertGroup?.contacts ? [...alertGroup.contacts] : []);
  }

  useEffect(() => {
    init();
  }, [alertGroup.id]);

  useEffect(() => {
    if (isEmpty(globalAlertsEnable)) return;

    const indoorErrors: any = {
      indoorUnitErrorEnable: globalAlertsEnable?.indoorUnitErrorEnable
    };
    const maintenance: any = {
      scheduleFailuresEnable: globalAlertsEnable?.scheduleFailuresEnable,
      cleanFilterEnable: globalAlertsEnable?.cleanFilterEnable,
      lowLineQualityEnable: globalAlertsEnable?.lowLineQualityEnable,
      bacnetUnitDisconnectedEnable: globalAlertsEnable?.bacnetUnitDisconnectedEnable,
      bacnetUnitErrorEnable: globalAlertsEnable?.bacnetUnitErrorEnable,
      bacnetUnitObjectReadingErrorEnable: globalAlertsEnable?.bacnetUnitObjectReadingErrorEnable,
      bacnetUnitObjectPriorityErrorEnable: globalAlertsEnable?.bacnetUnitObjectPriorityErrorEnable,
      modbusUnitErrorEnable: globalAlertsEnable?.modbusUnitErrorEnable,
      ppdHealthBadPowerMeterDataEnable: globalAlertsEnable?.ppdHealthBadPowerMeterDataEnable,
    };
    const communication: any = {
      deviceDisconnectedEnable: globalAlertsEnable?.deviceDisconnectedEnable,
      unitDisconnectedEnable: globalAlertsEnable?.unitDisconnectedEnable,
      sensorDisconnectedEnable: globalAlertsEnable?.sensorDisconnectedEnable,
      powerMeterDisconnectedEnable: globalAlertsEnable?.powerMeterDisconnectedEnable
    };
    const systemErrors: any = {
      outdoorUnitErrorEnable: globalAlertsEnable?.outdoorUnitErrorEnable
    };
    const newMap = {
      "System Error": { ...systemErrors },
      "Indoor Error": { ...indoorErrors },
      "Anomalies": allTraps,
      "Maintenance": { ...maintenance },
      "Communication": { ...communication },
      "Automation Logic": automationRules,
      "Smart Rules": smartRules
    };

    setSpecificAlertTypesMap(newMap);
  }, [globalAlertsEnable]);

  useEffect(() => {
    const globalAlerts = {
      deviceDisconnectedEnable: { isEnabled: !!alertGroup?.deviceDisconnectedEnable, title: t`Device Disconnect` },
      cleanFilterEnable: { isEnabled: !!alertGroup?.cleanFilterEnable, title: t`Clean Filter alerts (No SMS)` },
      indoorUnitErrorEnable: { isEnabled: !!alertGroup?.indoorUnitErrorEnable, title: t`Unit indoor alerts` },
      outdoorUnitErrorEnable: { isEnabled: !!alertGroup?.outdoorUnitErrorEnable, title: t`Unit Outdoor alerts` },
      unitDisconnectedEnable: { isEnabled: !!alertGroup?.unitDisconnectedEnable, title: t`Unit Disconnected` },
      lowLineQualityEnable: { isEnabled: !!alertGroup?.lowLineQualityEnable, title: t`Line Quality alerts` },
      scheduleFailuresEnable: { isEnabled: !!alertGroup?.scheduleFailuresEnable, title: t`Schedule Failures` },
      sensorDisconnectedEnable: { isEnabled: !!alertGroup?.sensorDisconnectedEnable, title: t`Sensor Disconnected` },
      powerMeterDisconnectedEnable: { isEnabled: !!alertGroup?.powerMeterDisconnectedEnable, title: t`Power Meter Disconnected` },
      bacnetUnitDisconnectedEnable: { isEnabled: !!alertGroup?.bacnetUnitDisconnectedEnable, title: t`BACnet device Disconnected` },
      bacnetUnitErrorEnable: { isEnabled: !!alertGroup?.bacnetUnitErrorEnable, title: t`BACnet device Error` },
      bacnetUnitObjectReadingErrorEnable: { isEnabled: !!alertGroup?.bacnetUnitObjectReadingErrorEnable, title: t`BACnet Device Object Read Error` },
      bacnetUnitObjectPriorityErrorEnable: { isEnabled: !!alertGroup?.bacnetUnitObjectPriorityErrorEnable, title: t`BACnet Device Object Priority Error` },
      modbusUnitErrorEnable: { isEnabled: !!alertGroup?.modbusUnitErrorEnable, title: t`Modbus Device Error` },
      ppdHealthBadPowerMeterDataEnable: { isEnabled: !!alertGroup?.ppdHealthBadPowerMeterDataEnable, title: t`Power Meter Data Out Of Range` },
    };

    setSelectedTrapPriorityType(alertGroup?.priority || alertGroup?.priorities?.[0] || 2);
    setNotifyBy((alertGroup?.type || alertGroupTypes.regular) + "");
    setGlobalAlertsEnable(globalAlerts);
  }, []);

  const isSpecificAlertsSelected = +notifyBy === alertGroupTypes.regular;
  const isIndoorOrOutdoorSelected = !!specificAlertTypesMap["System Error"]?.outdoorUnitErrorEnable?.isEnabled || !!specificAlertTypesMap["Indoor Error"]?.indoorUnitErrorEnable?.isEnabled;
  const shouldDisableFilterSpecificErrorCodes = isSpecificAlertsSelected && !isIndoorOrOutdoorSelected;
  const isSpecificAlertTypesMapReady = !isEmpty(specificAlertTypesMap);

  useEffect(() => {
    if (!isSpecificAlertTypesMapReady) {
      return;
    }
    if (shouldDisableFilterSpecificErrorCodes) {
      setFilterSpecificErrorCodes(false);
    }
  }, [isSpecificAlertTypesMapReady, shouldDisableFilterSpecificErrorCodes]);

  // Assembles the same payload shape that gets saved to the backend — reused to snapshot
  // the form's initial state and diff it against the current state to detect unsaved changes.
  const buildPayload = () => {
    const trapsIds = Object.keys(specificAlertTypesMap["Anomalies"] || {}).filter((trapId: string) => specificAlertTypesMap["Anomalies"][trapId]?.isEnabled);
    const automationRulesIds = Object.keys(specificAlertTypesMap["Automation Logic"] || {}).filter((ruleId: string) => specificAlertTypesMap["Automation Logic"][ruleId]?.isEnabled);
    const smartRuleIds = Object.keys(specificAlertTypesMap["Smart Rules"] || {}).filter((id: string) => specificAlertTypesMap["Smart Rules"][id]?.isEnabled);

    const enablesObject: any = Object.entries({
      ...specificAlertTypesMap["System Error"],
      ...specificAlertTypesMap["Indoor Error"],
      ...specificAlertTypesMap["Maintenance"],
      ...specificAlertTypesMap["Communication"]
    }).reduce((acc: any, [key, curVal]: any) => {
      acc[key] = !!curVal?.isEnabled;
      return acc;
    }, {});

    const data: any = +notifyBy === alertGroupTypes.regular ? {
      name,
      enablesObject,
      traps: trapsIds,
      allUserTrapsEnable,
      logicTraps: automationRulesIds,
      allLogicTrapsEnable,
      allSmartRulesEnable,
      smartRules: smartRuleIds,
    }
      : { name, priority: selectedTrapPriorityType };

    data.type = +notifyBy;
    data.aggregationEnabled = aggregationEnabled;
    data.aggregationPeriod = aggregationPeriod;
    data.sendCloseNotifications = sendCloseNotifications;
    data.smartAggregationEnabled = smartAggregationEnabled;
    data.enableFilters = filterSpecificErrorCodes;
    data.filters = filters;
    data.reportEnabled = reportEnabled;
    data.notificationsEnabled = notificationsEnabled;
    data.contacts = contacts.map(({ contact, sendSms, sendEmail, sendPush }: any) => ({
      contact,
      sendSms: !!sendSms,
      sendEmail: !!sendEmail,
      sendPush: !!sendPush,
    }));

    if (mustSendReportSchedule) {
      data.reportSchedule = buildReportSchedulePayload();
    }

    return data;
  };

  const baselinePayloadRef = useRef<any>(null);
  const baselineCapturedForRef = useRef<any>(null);

  useEffect(() => {
    if (!isSpecificAlertTypesMapReady || baselineCapturedForRef.current === alertGroup.id) {
      return;
    }
    baselineCapturedForRef.current = alertGroup.id;
    baselinePayloadRef.current = buildPayload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpecificAlertTypesMapReady, alertGroup.id]);

  const handleClose = async () => {
    const isDirty = !baselinePayloadRef.current || !_.isEqual(baselinePayloadRef.current, buildPayload());
    if (isDirty) {
      const ok = await confirm({
        title: t`Discard changes?`,
        bodyMessage: t`Are you sure you want to close without saving your changes?`,
        confirmText: t`Discard`,
        cancelText: t`Keep editing`,
      });
      if (!ok) return;
    }
    close();
  };

  const handleAgree = () => {

    trackEvent('NotificationsSettingsSaveGroup', 'The user created a Notification group');

    const b = Object.values(specificAlertTypesMap).map((rules: any) => Object.values(rules).findIndex((rule: any) => rule.isEnabled) !== -1).findIndex((x: boolean) => x) !== -1;

    if (!name) {
      setError(t`Required`);
      setErrorMessage("Name is required!");
      return;
    }
    if (+notifyBy === alertGroupTypes.regular && !b && !allLogicTrapsEnable && !allUserTrapsEnable) {
      setActiveStep("trigger");
      setErrorMessage("At least one alert, anomaly or automation logic is required");
      return;
    }

    const validateFilterResults: any = validateFilters(filters);

    if ((filterSpecificErrorCodes || filters?.length > 0) && !validateFilterResults.isValid) {
      setActiveStep("realtime");
      setErrorMessage(t`Missing or invalid filter(s) information.`);
      setFilterSpecificErrorCodesValidation(validateFilterResults);
      console.error('filters Error', filters)
      return;
    }

    setError("");
    setErrorMessage("");

    const data = buildPayload();

    isEdit
      ? saveAlert({ id: alertGroup.id, ...data })
      : saveAlert({ site: siteId, ...data });

    if (contactIdsToDelete.length) {
      Promise.allSettled(contactIdsToDelete.map((id) => deleteContactFromSite({ id }))).then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") {
            addMessage({ message: result.reason?.message });
          }
        });
      });
      const restContacts = { ...(userContacts || {}) };
      contactIdsToDelete.forEach((id) => { delete restContacts[id]; });
      setUserContacts(restContacts);
    }

    close();
  };
  const allAlertsIsSelected = !_.isEmpty(specificAlertTypesMap[specificAlertTypeSelected]) && Object.values(specificAlertTypesMap[specificAlertTypeSelected] || {}).every((alert: any) => alert?.isEnabled);

  const alertsToShow = useMemo(
    () => {
      if (isEmpty(specificAlertTypesMap[specificAlertTypeSelected])) {
        return [];
      }

      return Object.entries(specificAlertTypesMap[specificAlertTypeSelected])
        .filter(([_, value = {}]: any) =>
          (value.title || value.name)?.toLocaleLowerCase().includes(specificAlertSearchValue.toLocaleLowerCase())
        )
        .map(([key, value]) => ({ key, value }))
    },
    [specificAlertTypesMap, specificAlertTypeSelected, specificAlertSearchValue]
  );

  const toggleAllAlerts = () => {
    const updatedAlerts = cloneDeep(specificAlertTypesMap[specificAlertTypeSelected] || {});

    Object.keys(updatedAlerts).forEach((key) => {
      updatedAlerts[key].isEnabled = !allAlertsIsSelected;
    });

    setSpecificAlertTypesMap({
      ...specificAlertTypesMap,
      [specificAlertTypeSelected]: updatedAlerts,
    });
  };

  const handleFiltersChange = (filters: any) => {
    setFilters(filters);
  }

  const handleSmartAggregationChange = () => {
    const newValue = !smartAggregationEnabled;
    setSmartAggregationEnabled(newValue);
    if (newValue) {
      setAggregationEnabled(false);
    }
  }

  const goToStep = (step: StepId) => {
    setActiveStep(step);
    setVisitedSteps((prev) => ({ ...prev, [step]: true }));
  };

  // ---- Recipients helpers ----
  const siteObj = allSitesById?.[alertGroup?.site || siteId];
  const allowSms = !!(siteObj?.hvacAdvancedOperations || siteObj?.predictiveMaintenance || siteObj?.basicRemoteDiagnostics);
  // Per-brand kill switch. Absent means off, so a white-label never gets the
  // column by accident — only hosts that opt in explicitly.
  const allowPush = !!configFile?.enablePushNotifications;

  // contactId -> does this contact's email have a registered push device.
  const [pushAvailability, setPushAvailability] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!allowPush) {
      return;
    }
    const unknown = contacts
      .map((c: any) => c.contact)
      .filter((id: string) => id && !(id in pushAvailability));
    if (!unknown.length) {
      return;
    }
    let cancelled = false;
    Promise.all(
      unknown.map((id: string) =>
        sdkContact
          .getPushAvailability(id)
          // A lookup failure must not silently read as "has push" — a checkbox
          // the user can tick but that never delivers is worse than a disabled one.
          .then((res: any) => [id, !!res?.available] as [string, boolean])
          .catch(() => [id, false] as [string, boolean])
      )
    ).then((entries) => {
      if (cancelled) {
        return;
      }
      setPushAvailability((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });

    return () => {
      cancelled = true;
    };
  }, [allowPush, contacts, pushAvailability]);

  const recipientRows = useMemo(() => contacts
    .map((c: any) => ({ ...userContacts?.[c.contact], ...c }))
    .filter((row: any) => `${row.firstName || ''} ${row.lastName || ''}`.toLocaleLowerCase().includes(recipientSearch.toLocaleLowerCase())),
    [contacts, userContacts, recipientSearch]);

  const addableContacts = useMemo(() => {
    const addedIds = new Set(contacts.map((c: any) => c.contact));
    return _.sortBy(Object.values(userContacts || {}), [(c: any) => addedIds.has(c.id), (c: any) => c.firstName, (c: any) => c.lastName])
      .filter((c: any) => !contactIdsToDelete.includes(c.id))
      .filter((c: any) => `${c.firstName || ''} ${c.lastName || ''} ${c.email || ''}`.toLocaleLowerCase().includes(addRecipientsSearch.toLocaleLowerCase()))
      .map((c: any) => ({ ...c, isAdded: addedIds.has(c.id) }));
  }, [contacts, userContacts, addRecipientsSearch, contactIdsToDelete]);

  const hasAddableContacts = Object.keys(userContacts || {}).length > contacts.length;

  const toggleContactService = (contactId: string, service: "sendSms" | "sendEmail" | "sendPush") => {
    const contactData = userContacts?.[contactId];
    const current = contacts.find((c: any) => c.contact === contactId);
    if (service === "sendSms" && !current?.sendSms && isEmpty(contactData?.phone)) {
      confirm({
        title: t`Missing Phone Number`,
        bodyMessage: t`To get SMS notifications, you will need to add a phone number for this recipient. Double-click the contact to open their details and enter their phone number.`,
        confirmText: t`Ok`,
        cancelText: null,
      });
      return;
    }
    if (service === "sendEmail" && !current?.sendEmail && isEmpty(contactData?.email)) {
      confirm({
        title: t`Missing Email Address`,
        bodyMessage: t`To get E-mail notifications, you will need to add an email address for this recipient. Double-click the contact to open their details and enter their email.`,
        confirmText: t`Ok`,
        cancelText: null,
      });
      return;
    }
    setContacts((prev) => prev.map((c: any) => c.contact === contactId ? { ...c, [service]: !c[service] } : c));
  };

  const toggleContactToAdd = (contactId: string) => {
    setContactsToAdd((prev) => prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]);
  };

  const closeAddRecipients = () => {
    setAddingRecipient(false);
    setContactsToAdd([]);
    setAddRecipientsSearch("");
  };

  const addRecipients = () => {
    if (!contactsToAdd.length) {
      return;
    }
    trackEvent('NotificationsSettingsAddRecipients', 'The user added recipients to the Notification group');
    setContacts((prev) => [...prev, ...contactsToAdd.map((contactId) => ({ contact: contactId, sendSms: false, sendEmail: false, sendPush: false }))]);
    closeAddRecipients();
  };

  const removeRecipient = (contactId: string) => {
    setContacts((prev) => prev.filter((c: any) => c.contact !== contactId));
  };

  useEffect(() => {
    if (!addingRecipient || editContact || addingNewContact) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (recipientsToolbarRef.current && !recipientsToolbarRef.current.contains(event.target as Node)) {
        closeAddRecipients();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addingRecipient, editContact, addingNewContact]);

  // ---- Step summaries ----
  const enabledSpecificCount = useMemo(() => Object.values(specificAlertTypesMap)
    .reduce((acc: number, group: any) => acc + Object.values(group || {}).filter((alert: any) => alert?.isEnabled).length, 0),
    [specificAlertTypesMap]);

  const aggregationLabel = aggregationTimePeriods?.find((p: any) => p.value === aggregationPeriod)?.label || aggregationPeriod;
  const reportTimeLabel = `${pad(reportHour)}:${pad(reportMinute)}`;
  const monthDaysLabel = reportMonthMode === 'end'
    ? t`End of month`
    : `${t`Day`} ${reportDayOfMonth}`;
  const reportFrequencyLabel = reportTimeUnit === 'week'
    ? weekdayShortLabels[reportDayOfWeek]
    : reportTimeUnit === 'month'
      ? monthDaysLabel
      : t`Daily`;

  const stepDefined: Record<StepId, boolean> = {
    trigger: !isSpecificAlertsSelected || enabledSpecificCount > 0 || allUserTrapsEnable || allLogicTrapsEnable || allSmartRulesEnable,
    realtime: visitedSteps.realtime,
    report: visitedSteps.report || reportEnabled,
    recipients: contacts.length > 0,
  };

  const stepSummaries: Record<StepId, string> = {
    trigger: !isSpecificAlertsSelected
      ? `${t`Alert Priority`} • ${trapPriorityTypes?.[selectedTrapPriorityType] || ''}`
      : `${t`Specific Alerts`} • ${enabledSpecificCount} ${t`selected`}`,
    realtime: notificationsEnabled
      ? (aggregationEnabled ? `${t`On`} • ${t`Every`} ${aggregationLabel}` : t`On`)
      : t`Off`,
    report: reportEnabled
      ? `${reportFormat === 'csv' ? t`CSV` : t`Excel`} • ${reportFrequencyLabel} ${reportTimeLabel}`
      : t`Off`,
    recipients: contacts.length === 1 ? t`1 person` : `${contacts.length} ${t`people`}`,
  };

  const steps: { id: StepId; title: string }[] = [
    { id: "trigger", title: t`Trigger` },
    { id: "realtime", title: t`Notifications Options` },
    { id: "report", title: t`Summary Report` },
    { id: "recipients", title: t`Recipients` },
  ];

  // ---- Step content renderers ----
  const renderTriggerStep = () => (
    <>
      <Typography className={styles.contentTitle}>{t`What Should Trigger This Group?`}</Typography>
      <div className={styles.priorityRow}>
        <RadioButton
          checked={notifyBy === '2'}
          onClick={() => setNotifyBy('2')}
          label={t`Alert Priority`}
        />
        <div className={styles.checkboxRow}>
          <Select
            disabled={+notifyBy === alertGroupTypes.regular}
            className={styles.prioritySelect}
            options={Object.entries(trapPriorityTypes).map(([key, value]: any) => ({ value: Number(key), label: value }))}
            value={selectedTrapPriorityType}
            onChange={(value: number) => setSelectedTrapPriorityType(value)}
            menuPortal
            size="medium"
          />
          <Typography className={styles.mutedText}>{t`and above`}</Typography>
        </div>
      </div>

      <Divider />

      <RadioButton
        checked={notifyBy === '1'}
        onClick={() => setNotifyBy('1')}
        label={t`Specific Alerts`}
      />

      <div className={clsx(styles.specificAlertsSection, { [styles.disabledDiv]: +notifyBy !== alertGroupTypes.regular })}>
        <div className={styles.categoriesList}>
          {specificAlertTypes.map((title) => (
            <div
              key={title}
              className={clsx(styles.categoryItem, specificAlertTypeSelected === title && styles.categoryItemSelected)}
              onClick={() => setSpecificAlertTypeSelected(title)}
            >
              {`${title} (${Object.keys(specificAlertTypesMap[title] || {}).filter((key) => specificAlertTypesMap[title]?.[key]?.isEnabled).length})`}
            </div>
          ))}
        </div>
        <div className={styles.alertsPane}>
          <div className={styles.searchAndSelectAll}>
            <div className={styles.searchInput}>
              <SearchInput
                placeholder={t`Search..`}
                autoFocus={false}
                onChange={(value: string) => setSpecificAlertSearchValue(value || "")}
              />
            </div>
            <Button variant="text" className={styles.selectAllLink} onClick={toggleAllAlerts}>
              {allAlertsIsSelected ? t`Unselect All` : t`Select All`}
            </Button>
          </div>
          <div className={styles.alertsList}>
            {alertsToShow.map(({ key, value }: any) => (
              <div key={key} className={styles.alertListItem}>
                <Checkbox
                  checked={!!value.isEnabled}
                  onChange={() => {
                    const newMap = cloneDeep(specificAlertTypesMap);
                    newMap[specificAlertTypeSelected][key].isEnabled = !value.isEnabled;
                    setSpecificAlertTypesMap(newMap);
                  }}
                  size="small"
                />
                <Typography>{value.title || value.name}</Typography>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const renderRealTimeStep = () => (
    <>
      <Typography className={styles.contentTitle}>{t`Notifications Options`}</Typography>
      <div className={styles.switchRow}>
        <Typography className={styles.switchLabel}>{t`Send Alert Notifications`}</Typography>
        <CoolSwitch checked={notificationsEnabled} onChange={() => setNotificationsEnabled(!notificationsEnabled)} />
      </div>
      <div className={clsx(styles.indented, !notificationsEnabled && styles.disabledDiv)}>
      <Divider />
       <Tooltip
          title={smartAggregationEnabled ? t`Please Note: Aggregation only applies on System Errors. Please be minded to the way you set all other Alerts` : aggregationEnabled ? t`To select this option first uncheck ‘Send notifications with alert status update every xx minutes..’. They can not be set together.` : ''}
          placement="top"
       >
        <span>
          <div className={clsx(styles.checkboxRow, notificationsEnabled && aggregationEnabled && styles.disabledDiv)}>
            <Checkbox
              checked={smartAggregationEnabled}
              onChange={handleSmartAggregationChange}
              disabled={!notificationsEnabled || aggregationEnabled}
            />
            <Typography variant="body2">{t`Aggregate System Error events into a single Alert event`}</Typography>
          </div>
        </span>
      </Tooltip>
      <Divider />
      <div className={styles.checkboxRow}>
        <Checkbox
            checked={sendCloseNotifications}
            onChange={() => setSendCloseNotifications(!sendCloseNotifications)}
            disabled={!notificationsEnabled}
        />
        <Typography variant="body2">{t`Send notifications when alert status changed to closed`}</Typography>
      </div>
      <Divider />
         <Tooltip
            title={smartAggregationEnabled ? t`To select this option first uncheck 'Aggregate System Error events into a single Alert event'. They can not be set together.` : ""}
            placement="top"
         >
          <span>
            <div className={clsx(styles.checkboxRow, notificationsEnabled && smartAggregationEnabled && styles.disabledDiv)}>
              <Checkbox
                checked={aggregationEnabled}
                onChange={() => setAggregationEnabled(!aggregationEnabled)}
                disabled={!notificationsEnabled || smartAggregationEnabled}
              />
              <Typography variant="body2">{t`Send notifications with alert status update every`}</Typography>
                <Select
                value={aggregationPeriod}
                onChange={(value: string) => setAggregationPeriod(value)}
                className={styles.aggregationSelect}
                disabled={!aggregationEnabled}
                options={aggregationTimePeriods?.map(({ value, label }: { value: string; label: string }) => ({ value, label })) || []}
                menuPortal
                size="medium"
              />
            </div>
          </span>
        </Tooltip>
        <Divider />
        <div className={clsx(styles.filteroutSection, (notificationsEnabled && shouldDisableFilterSpecificErrorCodes) && styles.disabledDiv)}>
          <FilterOutSpecificErrorCodesForm
            enabled={filterSpecificErrorCodes}
            filters={filters}
            onEnable={setFilterSpecificErrorCodes}
            onFilterChange={handleFiltersChange}
            validationStatus={filterSpecificErrorCodesValidation}
            disabled={!notificationsEnabled || shouldDisableFilterSpecificErrorCodes}
          />
        </div>
      </div>
    </>
  );

  const renderReportStep = () => (
    <>
      <Typography className={styles.contentTitle}>{t`Periodic Summary Report`}</Typography>
      <div className={styles.switchRow}>
        <Typography className={styles.switchLabel}>{t`Send Periodic Summary Report`}</Typography>
        <CoolSwitch checked={reportEnabled} onChange={() => setReportEnabled(!reportEnabled)} />
      </div>

      {reportEnabled && (
        <>
          <div className={styles.fieldsRow}>
            <div className={styles.fieldColumn}>
              <Typography className={styles.fieldLabel}>{t`File Format`}</Typography>
              <Select
                options={reportFormatOptions}
                value={reportFormat}
                onChange={(value: ReportFormat) => { setReportFormat(value); markReportScheduleTouched(); }}
                menuPortal
              />
            </div>
          </div>

          <div className={styles.fieldColumn}>
            <Typography className={styles.fieldLabel}>{t`Report Frequency`}</Typography>
            <Segmented
              options={reportFrequencyOptions}
              value={reportTimeUnit}
              onChange={(value: string) => { setReportTimeUnit(value as ReportTimeUnit); markReportScheduleTouched(); }}
            />
          </div>

          {reportTimeUnit === 'week' && (
            <div className={styles.fieldColumn}>
              <Typography className={styles.fieldLabel}>{t`Day of Week`}</Typography>
              <DaysList
                days={WEEK_DAYS}
                value={[WEEK_DAYS[reportDayOfWeek]]}
                onChange={(day: string) => { setReportDayOfWeek(WEEK_DAYS.indexOf(day)); markReportScheduleTouched(); }}
                white={false}
                className={styles.weekDays}
              />
            </div>
          )}

          {reportTimeUnit === 'month' && (
            <div className={styles.fieldColumn}>
              <div className={styles.monthModeContainer}>
                <RadioButton
                  checked={reportMonthMode === 'end'}
                  onClick={() => { setReportMonthMode('end'); markReportScheduleTouched(); }}
                  label={t`End Of Month`}
                />
                <Tooltip
                  title={t`If this day doesn't exist in a month, we'll use the last day instead.`}
                  placement="top"
                >
                  <span>
                    <RadioButton
                      checked={reportMonthMode === 'specific'}
                      onClick={() => { setReportMonthMode('specific'); markReportScheduleTouched(); }}
                      label={t`Specific Day`}
                    />
                  </span>
                </Tooltip>
              </div>
              {reportMonthMode === 'specific' && (
                <div>
                  <Typography className={styles.noteText}>{t`Summarizing since same date of last month.`}</Typography>
                  <Select
                    className={styles.daysOfMonthSelect}
                    options={dayOfMonthOptions}
                    value={reportDayOfMonth}
                    onChange={(value: number) => { setReportDayOfMonth(value); markReportScheduleTouched(); }}
                    menuPortal
                  />
                </div>
              )}
            </div>
          )}

          <div className={styles.fieldColumn}>
            <Typography className={styles.fieldLabel}>{t`At Time`}</Typography>
            <div className={styles.timePickerHolder}>
              <TimePicker
                value={reportTimeLabel}
                is12Hours={is12Hours}
                open={isTimePickerOpen}
                onOpenChange={setIsTimePickerOpen}
                valuePillClassName={styles.timePickerValue}
                onChange={(time: string) => {
                  const [hour, minute] = time.split(':').map(Number);
                  setReportHour(hour || 0);
                  setReportMinute(minute || 0);
                  markReportScheduleTouched();
                }}
              />
            </div>
          </div>
        </>
      )}
    </>
  );

  const renderRecipientsStep = () => (
    <>
      <Typography className={styles.contentTitle}>{t`Who receives these alerts?`}</Typography>
      <div className={styles.recipientsToolbar} ref={recipientsToolbarRef}>
        <div className={styles.recipientsSearch}>
          <SearchInput
            placeholder={t`Search recipients`}
            autoFocus={false}
            onChange={(value: string) => setRecipientSearch(value || "")}
          />
        </div>
        <Button
          variant="text"
          className={styles.addRecipientsLink}
          onClick={() => setAddingRecipient(true)}
        >
          {t`+ Add Recipients`}
        </Button>
        {addingRecipient && (
          <div className={styles.addRecipientsPanel}>
            <SearchInput
              placeholder={t`Search platform users to add...`}
              autoFocus
              onChange={(value: string) => setAddRecipientsSearch(value || "")}
            />
            <div className={styles.addRecipientsList}>
              {addableContacts.map((contact: any) => {
                const initials = `${contact.firstName?.[0] || ''}${contact.lastName?.[0] || ''}`.toUpperCase();
                const checked = contact.isAdded || contactsToAdd.includes(contact.id);
                const handleRemoveClick = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (contact.isAdded) {
                    removeRecipient(contact.id);
                  } else if (contactsToAdd.includes(contact.id)) {
                    toggleContactToAdd(contact.id);
                  }
                  setContactIdsToDelete((prev) => prev.includes(contact.id) ? prev : [...prev, contact.id]);
                };
                return (
                  <div
                    key={contact.id}
                    className={clsx(styles.addRecipientRow, contact.isAdded && styles.addRecipientRowAdded)}
                    onClick={() => !contact.isAdded && toggleContactToAdd(contact.id)}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        disabled={contact.isAdded}
                        value={contact.id}
                        onChange={() => toggleContactToAdd(contact.id)}
                      />
                    </div>
                    <div className={styles.addRecipientAvatar}>{initials}</div>
                    <Typography className={styles.addRecipientName}>{`${contact.firstName || ''} ${contact.lastName || ''}`}</Typography>
                    {contact.isAdded ? (
                      <Typography className={styles.addRecipientAdded}>{t`Added`}</Typography>
                    ) : (
                      <Typography className={styles.addRecipientEmail}>{contact.email}</Typography>
                    )}
                    <IconButton
                      size="small"
                      onClick={handleRemoveClick}
                      className={styles.removeRecipientBtn}
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                );
              })}
            </div>
            <div className={styles.addRecipientsFooter}>
              <Button variant="text" className={styles.addRecipientsCreateNew} onClick={() => setAddingNewContact(true)}>
                {t`Create New`}
              </Button>
              <div className={styles.addRecipientsFooterActions}>
                <Button variant="text" className={styles.addRecipientsCancel} onClick={closeAddRecipients}>
                  {t`Cancel`}
                </Button>
                <Button onClick={addRecipients} disabled={contactsToAdd.length === 0}>
                  {t`Add (${contactsToAdd.length})`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles.recipientsTableHolder}>
        <Table
          data={recipientRows}
          getRowKey={(row: any) => row.contact}
          onRowDoubleClick={(row: any) => setEditContact(row)}
          emptyMessage={t`No recipients yet. Use "+ Add Recipients" to add contacts to this group.`}
          columns={[
            {
              id: 'name',
              title: t`RECIPIENTS`,
              render: (row: any) => <Typography>{`${row.firstName || ''} ${row.lastName || ''}`}</Typography>,
            },
            {
              id: 'sms',
              title: t`SMS`,
              align: 'center',
              render: (row: any) => (
                <Checkbox
                  checked={!!row.sendSms}
                  disabled={!allowSms}
                  value={row.contact}
                  onChange={(contactId: string) => toggleContactService(contactId, 'sendSms')}
                />
              ),
            },
            {
              id: 'email',
              title: t`E-MAIL`,
              align: 'center',
              render: (row: any) => (
                <Checkbox
                  checked={!!row.sendEmail}
                  value={row.contact}
                  onChange={(contactId: string) => toggleContactService(contactId, 'sendEmail')}
                />
              ),
            },
            ...(allowPush
              ? [
                {
                  id: 'push',
                  // Labelled IN-APP to match the design; the stored field is
                  // sendPush, which is what the backend delivers on.
                  title: t`IN-APP`,
                  align: 'center' as const,
                  render: (row: any) => {
                    const hasPush = !!pushAvailability[row.contact];
                    const checkbox = (
                      <Checkbox
                        checked={!!row.sendPush}
                        disabled={!hasPush}
                        value={row.contact}
                        onChange={(contactId: string) => toggleContactService(contactId, 'sendPush')}
                      />
                    );
                    // Explain the disabled state; without it the column just
                    // looks broken for contacts who never opened the app.
                    return hasPush ? checkbox : (
                      <Tooltip title={t`Recipient needs to allow notifications on mobile for pro app`}>
                        <span>{checkbox}</span>
                      </Tooltip>
                    );
                  },
                },
              ]
              : []),
            {
              id: 'remove',
              title: '',
              align: 'right',
              render: (row: any) => (
                <IconButton onClick={() => removeRecipient(row.contact)}>
                  <CloseIcon />
                </IconButton>
              ),
            },
          ]}
        />
      </div>
    </>
  );

  const stepContent: Record<StepId, () => JSX.Element> = {
    trigger: renderTriggerStep,
    realtime: renderRealTimeStep,
    report: renderReportStep,
    recipients: renderRecipientsStep,
  };

  return (
    <>
      <BaseDialog
        open
        onClose={handleClose}
        onConfirm={handleAgree}
        title={isEdit ? t`Edit Group` : t`Add Group`}
        confirmLabel={t`Save`}
        cancelLabel={t`Cancel`}
        size="xlarge"
        confirmDisabled={!name?.trim()}
        footerLeftContent={!!errorMessage && <Typography color="error">{errorMessage}</Typography>}
        containerClassName={styles.dialogContent}
        className={styles.alertGroupDialog}
      >
        <div className={styles.nameRow}>
          <TextField
            placeholder={t`Enter Group Name`}
            id={"input-alert-group-name"}
            value={name}
            onChange={(value: string) => setName(value)}
            error={!!error}
            className={styles.nameField}
          />
        </div>
        <div className={styles.body}>
          <nav className={styles.stepNav}>
            {steps.map((step, index) => {
              const isActive = activeStep === step.id;
              const isDefined = stepDefined[step.id];
              return (
                <div
                  key={step.id}
                  className={clsx(styles.stepCard, isDefined && styles.stepCardDefined, isActive && styles.stepCardActive)}
                  onClick={() => goToStep(step.id)}
                  role="button"
                  tabIndex={0}
                >
                  <div className={clsx(styles.stepCircle, (isDefined || isActive) && styles.stepCircleDone)}>{index + 1}</div>
                  <div className={styles.stepTexts}>
                    <span className={styles.stepTitle}>{step.title}</span>
                    <span className={styles.stepSubtitle}>{isDefined ? stepSummaries[step.id] : t`Not Defined`}</span>
                  </div>
                </div>
              );
            })}
          </nav>
          <div className={styles.content}>
            {stepContent[activeStep]()}
          </div>
        </div>
      </BaseDialog>
      {(editContact || addingNewContact) && (
        <AddEditContact
          editContact={editContact}
          localContacts={userContacts}
          setLocalContacts={setUserContacts}
          siteId={siteId}
          customerId={customerId}
          onClose={() => { setEditContact(null); setAddingNewContact(false); }}
        />
      )}
    </>
  );
};
export default AlertGroup;
